import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { PWAInstallPrompt, PWAState, ServiceWorkerMessage } from './pwa.types';

@Injectable({
  providedIn: 'root'
})
export class PwaService {
  private deferredPrompt: PWAInstallPrompt | null = null;
  private installPromptSubject = new BehaviorSubject<boolean>(false);
  private updateAvailableSubject = new BehaviorSubject<boolean>(false);
  private pwaStatusSubject = new BehaviorSubject<boolean>(false);
  private isIOS = false;
  private isStandalone = false;
  private swRegistration: ServiceWorkerRegistration | null = null;

  constructor() {
    this.initializePWA();
    this.detectPlatform();
    this.fixIOSScroll();
  }

  private detectPlatform(): void {
    // Detectar iOS
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                 (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    // Detectar si está en modo standalone (PWA instalada)
    this.isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                       (window.navigator as any).standalone === true;

    // Actualizar el estado PWA
    this.pwaStatusSubject.next(this.isStandalone);

    // // // console.log('Platform Detection:', {
    //   isIOS: this.isIOS,
    //   isStandalone: this.isStandalone,
    //   userAgent: navigator.userAgent
    // });
  }

  private fixIOSScroll(): void {
    if (this.isIOS) {
      // Forzar el scroll en iOS
      setTimeout(() => {
        // Asegurar que el body tenga scroll habilitado
        document.body.style.position = 'relative';
        document.body.style.height = 'auto';
        document.body.style.overflowY = 'auto';

        // Usar setProperty para propiedades webkit
        document.body.style.setProperty('-webkit-overflow-scrolling', 'touch');

        // Asegurar que el html tenga scroll habilitado
        document.documentElement.style.position = 'relative';
        document.documentElement.style.height = 'auto';
        document.documentElement.style.overflowY = 'auto';
        document.documentElement.style.setProperty('-webkit-overflow-scrolling', 'touch');

        // // // console.log('iOS Scroll Fix Applied');
      }, 100);
    }
  }

  private async initializePWA(): Promise<void> {
    // Detectar si la PWA ya está instalada
    if (this.isStandalone) {
      // // // console.log('PWA ya está instalada');
    }

    // Escuchar el evento beforeinstallprompt (no disponible en iOS)
    window.addEventListener('beforeinstallprompt', (event: Event) => {
      event.preventDefault();
      this.deferredPrompt = event as unknown as PWAInstallPrompt;
      this.installPromptSubject.next(true);
    });

    // Escuchar cuando la PWA se instala
    window.addEventListener('appinstalled', (event: Event) => {
      // // // console.log('PWA instalada exitosamente');
      this.installPromptSubject.next(false);
      this.deferredPrompt = null;
    });

    // Inicializar Service Worker
    await this.initializeServiceWorker();
  }

  private async initializeServiceWorker(): Promise<void> {
    if ('serviceWorker' in navigator) {
      try {
        // Obtener el registro del service worker
        const registration = await navigator.serviceWorker.getRegistration();
        this.swRegistration = registration || null;
        
        if (this.swRegistration) {
          // Escuchar cambios en el service worker
          this.swRegistration.addEventListener('updatefound', () => {
            // // // console.log('Service Worker update found');
            this.checkForWaitingWorker();
          });

          // Escuchar cuando el service worker cambia
          navigator.serviceWorker.addEventListener('controllerchange', () => {
            // // // console.log('Service Worker controller changed');
            this.updateAvailableSubject.next(false);
            // Recargar la página para aplicar la nueva versión
            window.location.reload();
          });

          // Verificar si hay un worker esperando
          this.checkForWaitingWorker();
        }
      } catch (error) {
        console.warn('Error al inicializar Service Worker:', error);
      }
    }
  }

  private checkForWaitingWorker(): void {
    if (this.swRegistration && this.swRegistration.waiting) {
      // // // console.log('Hay un Service Worker esperando para actualizar');
      this.updateAvailableSubject.next(true);
    }
  }

  public async installPWA(): Promise<boolean> {
    if (this.isIOS) {
      // En iOS, mostrar instrucciones para instalar manualmente
      this.showIOSInstallInstructions();
      return false;
    }

    if (this.deferredPrompt) {
      this.deferredPrompt.prompt();
      const { outcome } = await this.deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        // // // console.log('Usuario aceptó instalar la PWA');
        this.deferredPrompt = null;
        this.installPromptSubject.next(false);
        return true;
      } else {
        // // // console.log('Usuario rechazó instalar la PWA');
        return false;
      }
    }
    return false;
  }

  private showIOSInstallInstructions(): void {
    // Crear modal con instrucciones para iOS
    const modal = document.createElement('div');
    modal.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0,0,0,0.8);
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
    `;

    modal.innerHTML = `
      <div style="
        background: white;
        padding: 20px;
        border-radius: 10px;
        max-width: 400px;
        margin: 20px;
        text-align: center;
      ">
        <h3>Instalar en iOS</h3>
        <p>Para instalar esta aplicación en tu iPhone o iPad:</p>
        <ol style="text-align: left;">
          <li>Toca el botón <strong>Compartir</strong> <span style="font-size: 20px;">📤</span></li>
          <li>Selecciona <strong>"Agregar a Pantalla de Inicio"</strong></li>
          <li>Toca <strong>"Agregar"</strong></li>
        </ol>
        <button onclick="this.parentElement.parentElement.remove()" style="
          background: #0d6efd;
          color: white;
          border: none;
          padding: 10px 20px;
          border-radius: 5px;
          margin-top: 15px;
          cursor: pointer;
        ">Entendido</button>
      </div>
    `;

    document.body.appendChild(modal);
  }

  public async checkForUpdate(): Promise<void> {
    if ('serviceWorker' in navigator && this.swRegistration) {
      try {
        // Verificar actualizaciones del service worker directamente
        await this.swRegistration.update();
        
        // Verificar si hay un worker esperando después de la verificación
        setTimeout(() => {
          this.checkForWaitingWorker();
        }, 1000);
      } catch (error) {
        console.warn('Error al verificar actualizaciones:', error);
      }
    }
  }

  public async activateUpdate(): Promise<void> {
    if ('serviceWorker' in navigator && this.swRegistration && this.swRegistration.waiting) {
      try {
        // Enviar mensaje al service worker para activar la actualización
        this.swRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
        
        // Esperar un momento para que el service worker procese el mensaje
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Recargar la página para aplicar la nueva versión
        window.location.reload();
      } catch (error) {
        console.error('Error al activar la actualización:', error);
        throw error;
      }
    } else {
      throw new Error('No hay actualizaciones disponibles para activar');
    }
  }

  public isPWAInstalled(): boolean {
    return this.isStandalone;
  }

  public canInstallPWA(): boolean {
    if (this.isIOS) {
      // En iOS siempre se puede mostrar las instrucciones
      return !this.isStandalone;
    }
    return this.deferredPrompt !== null;
  }

  public isIOSDevice(): boolean {
    return this.isIOS;
  }

  public getInstallPromptObservable(): Observable<boolean> {
    return this.installPromptSubject.asObservable();
  }

  public getUpdateAvailableObservable(): Observable<boolean> {
    return this.updateAvailableSubject.asObservable();
  }

  public getPWAStatusObservable(): Observable<boolean> {
    return this.pwaStatusSubject.asObservable();
  }

  public clearUpdateAvailable(): void {
    this.updateAvailableSubject.next(false);
  }

  public getInstallInstructions(): string {
    if (this.isIOS) {
      return 'Toca el botón Compartir 📤 y selecciona "Agregar a Pantalla de Inicio"';
    }
    return 'Haz clic en "Instalar" para agregar la aplicación';
  }

  public forceIOSScrollFix(): void {
    if (this.isIOS) {
      this.fixIOSScroll();
    }
  }
}
